import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ClipboardCheck,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const STATUS_META = {
  pass: { Icon: CheckCircle2, color: '#00C896', label: 'PASS' },
  warn: { Icon: AlertTriangle, color: '#FFA000', label: 'WARN' },
  fail: { Icon: XCircle,       color: '#EF4444', label: 'FAIL' },
  skip: { Icon: AlertTriangle, color: '#64748B', label: 'SKIP' },
};

/**
 * QBTestSyncDialog — admin preflight for a single job → QuickBooks invoice.
 * Calls /api/qb/test-sync/{jobId} which is read-only (never creates an
 * invoice). Renders the resulting checklist.
 */
export default function QBTestSyncDialog({ open, onOpenChange, job, token }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !job?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setResult(null);
    axios
      .post(
        `${API}/qb/test-sync/${job.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((r) => { if (!cancelled) setResult(r.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Test sync failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, job?.id, token]);

  const passes = result?.checks?.filter((c) => c.status === 'pass').length || 0;
  const warns  = result?.checks?.filter((c) => c.status === 'warn').length || 0;
  const fails  = result?.checks?.filter((c) => c.status === 'fail').length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl bg-[#0F1923] border border-[#2D3748] text-white max-h-[85vh] overflow-y-auto"
        data-testid="qb-test-sync-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <ClipboardCheck className="w-5 h-5" style={{ color: '#1E88E5' }} />
            Test sync — {job?.title || 'Job'}
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: '#94A3B8' }}>
            Dry-run preflight against QuickBooks. Nothing is created — this just
            checks each step the real sync would take and reports what passes,
            warns, or fails.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-8 text-sm" style={{ color: '#64748B' }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Running checks against QuickBooks…
          </div>
        )}

        {error && !loading && (
          <div
            className="flex items-start gap-3 p-4 rounded-lg"
            style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)' }}
            data-testid="qb-test-sync-error"
          >
            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#EF4444' }} />
            <div className="text-sm" style={{ color: '#FCA5A5' }}>
              <p className="font-semibold mb-1">Couldn't run test sync</p>
              <p className="text-xs whitespace-pre-wrap" style={{ color: '#F87171' }}>{error}</p>
            </div>
          </div>
        )}

        {result && !loading && (
          <>
            {/* Summary banner */}
            <div
              className="flex items-center gap-3 p-3 rounded-lg mt-2"
              style={{
                background: result.ok ? 'rgba(0,200,150,0.10)' : 'rgba(239,68,68,0.10)',
                border: `1px solid ${result.ok ? 'rgba(0,200,150,0.30)' : 'rgba(239,68,68,0.30)'}`,
              }}
              data-testid="qb-test-sync-summary"
            >
              {result.ok ? (
                <CheckCircle2 className="w-5 h-5" style={{ color: '#00C896' }} />
              ) : (
                <XCircle className="w-5 h-5" style={{ color: '#EF4444' }} />
              )}
              <div className="text-sm">
                <p className="font-semibold" style={{ color: result.ok ? '#00C896' : '#EF4444' }}>
                  {result.ok ? 'Ready to sync' : 'Cannot sync yet'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
                  {passes} passed · {warns} warning{warns === 1 ? '' : 's'} · {fails} fail{fails === 1 ? '' : 's'}
                  {result.ok && warns > 0 && ' — warnings will be auto-resolved at sync time.'}
                </p>
              </div>
            </div>

            <ul className="mt-3 space-y-2" data-testid="qb-test-sync-checks">
              {result.checks.map((c, i) => {
                const meta = STATUS_META[c.status] || STATUS_META.skip;
                const Icon = meta.Icon;
                return (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-lg"
                    style={{ background: '#1A2433', border: '1px solid #2D3748' }}
                    data-testid={`qb-test-sync-check-${i}`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: meta.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white">{c.check}</p>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                          style={{
                            background: `${meta.color}22`,
                            color: meta.color,
                          }}
                        >
                          {meta.label}
                        </span>
                      </div>
                      {c.detail && (
                        <p className="text-xs mt-1 whitespace-pre-wrap" style={{ color: '#94A3B8' }}>
                          {c.detail}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <DialogFooter className="mt-4">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ background: '#1E88E5', color: '#fff' }}
            data-testid="qb-test-sync-close"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
